<template>
    <modal id="attendance-break-time-modal"
           size="large"
           v-model="showModal"
           :title="$t('manage_break_time')"
           :loading="loading"
           :preloader="preloader"
           @submit="saveAllBreakTimes"
           @close-modal="closeModal"
           :scrollable="false"
    >

        <div class="mb-4">
            <!-- Break Time Rows -->
            <div v-if="breakTimeRows.length === 0" class="text-center text-muted py-4">
                <app-icon name="coffee" class="size-40 mb-2"/>
                <p>{{ $t('no_break_times_found') }}</p>
            </div>

            <div v-for="(row, index) in breakTimeRows" :key="index" class="card mb-2">
                <div class="card-body py-2">
                    <div class="row align-items-center">
                        <div class="col-md-4">
                            <app-input
                                type="select"
                                :label="$t('break_type')"
                                :list="breakTypeList"
                                v-model="row.break_time_id"
                                @input="handleBreakChange(index)"
                                :error-message="getError(index, 'break_time_id')"
                            />
                        </div>
                        <div class="col-md-3">
                            <app-input
                                type="time"
                                :label="$t('start_time')"
                                v-model="row.start_at"
                                @input="handleBreakChange(index)"
                                :error-message="getError(index, 'start_at')"
                            />
                        </div>
                        <div class="col-md-3">
                            <app-input
                                type="time"
                                :label="$t('end_time')"
                                v-model="row.end_at"
                                :error-message="getError(index, 'end_at')"
                            />
                        </div>
                        <div class="col-md-2 d-flex align-items-end">
                            <button type="button" 
                                    class="btn btn-danger btn-sm"
                                    @click="removeBreakTimeRow(index)">
                                <app-icon name="minus" class="size-16"/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add Button and Total Break Time Row -->
            <div class="mt-3 d-flex justify-content-between align-items-center">
                <!-- <button type="button" 
                        class="btn btn-sm btn-primary"
                        @click="addBreakTimeRow">
                    <app-icon name="plus" class="size-16 mr-1"/>
                    {{ $t('add_break_time') }}
                </button> -->
                <div></div>
                
                <div v-if="breakTimeRows.length > 0">
                    <strong class="mr-2">{{ $t('total_break_time') }}: </strong>
                    <span class="badge badge-lg badge-primary">{{ totalBreakTime }}</span>
                </div>
            </div>
        </div>

    </modal>
</template>

<script>
import ModalMixin from "../../../../../common/Mixin/Global/ModalMixin";
import FormHelperMixins from "../../../../../common/Mixin/Global/FormHelperMixins";
import {
    convertSecondToHourMinutes,
    dateTimeToLocalWithFormat,
    formatDateTimeForServer,
    onlyTime
} from "../../../../../common/Helper/Support/DateTimeHelper";
import {addChooseInSelectArray} from "../../../../../common/Helper/Support/FormHelper";
import {TENANT_BASE_URL} from "../../../../../common/Config/UrlHelper";
import moment from "moment";

export default {
    name: "AttendanceBreakTimeModal",
    mixins: [ModalMixin, FormHelperMixins],
    props: {
        attendanceDetail: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            breakTimeRows: [],
            breakTypeList: [],
            errors: {},
            isInitializingBreakRows: false
        }
    },
    mounted() {
        this.loadBreakTimes();
        this.loadBreakTypeList();
    },
    computed: {
        totalBreakTime() {
            let totalSeconds = 0;
            this.breakTimeRows.forEach(row => {
                if (row.start_at && row.end_at) {
                    const start = moment(row.start_at, 'HH:mm');
                    const end = moment(row.end_at, 'HH:mm');
                    if (end.isAfter(start)) {
                        totalSeconds += end.diff(start, 'seconds');
                    }
                }
            });
            return convertSecondToHourMinutes(totalSeconds);
        }
    },
    methods: {
        loadBreakTimes() {
            this.preloader = true;
            this.isInitializingBreakRows = true;
            this.axiosGet(`${TENANT_BASE_URL}app/attendance-details/${this.attendanceDetail.id}/break-times`)
                .then(response => {
                    // Convert existing break times to row format, preserve original values
                    this.breakTimeRows = response.data.map(bt => ({
                        id: bt.id,
                        break_time_id: bt.break_time_id,
                        start_at: this.extractTime(bt.start_at),
                        end_at: this.extractTime(bt.end_at),
                        // Store original values from server to preserve seconds
                        original_start_at: bt.start_at,
                        original_end_at: bt.end_at
                    }));
                    
                    // If no break times exist, add one empty row
                    if (this.breakTimeRows.length === 0) {
                        this.addBreakTimeRow();
                    }
                }).finally(() => {
                    this.preloader = false;
                    this.$nextTick(() => {
                        this.isInitializingBreakRows = false;
                    });
                });
        },
        loadBreakTypeList() {
            this.axiosGet(`${TENANT_BASE_URL}selectable/break-times`)
                .then(response => {
                    const list = response.data.map(bt => ({
                        id: bt.id,
                        value: bt.name,
                        duration: bt.duration
                    }));
                    this.breakTypeList = addChooseInSelectArray(list, 'value', 'break_type');
                });
        },
        addBreakTimeRow() {
            this.breakTimeRows.push({
                id: null,
                break_time_id: '',
                start_at: null,
                end_at: null
            });
        },
        removeBreakTimeRow(index) {
            this.breakTimeRows.splice(index, 1);
        },
        handleBreakChange(index) {
            if (this.isInitializingBreakRows) {
                return;
            }
            this.$nextTick(() => {
                const row = this.breakTimeRows[index];
                if (row.break_time_id && row.start_at) {
                    const breakType = this.breakTypeList.find(b => String(b.id) === String(row.break_time_id));
                    if (breakType && breakType.duration) {
                        const durationParts = String(breakType.duration).split(':');
                        const hours = parseInt(durationParts[0], 10) || 0;
                        const minutes = parseInt(durationParts[1], 10) || 0;
                        
                        const start = moment(row.start_at, ['HH:mm', 'h:mm A', 'hh:mm A', 'hh:mm a']);
                        if (start.isValid()) {
                            const end = start.clone().add(hours, 'hours').add(minutes, 'minutes');
                            const timeFormat = row.start_at.match(/am|pm/i) ? 'hh:mm A' : 'HH:mm';
                            row.end_at = end.format(timeFormat);
                        }
                    }
                }
            });
        },
        saveAllBreakTimes() {
            this.fieldStatus.isSubmit = true;
            this.loading = true;
            this.errors = {};
            
            // Prepare data for saving
            const data = {
                break_times: this.breakTimeRows.map(row => {
                    const startAt = row.start_at ? this.getTimeForSubmit(row, 'start_at') : null;

                    return {
                        id: row.id,
                        break_time_id: row.break_time_id,
                        // Use original value if time hasn't changed, otherwise convert the new time
                        start_at: startAt,
                        end_at: row.end_at ? this.getTimeForSubmit(row, 'end_at', {notBefore: startAt}) : null
                    };
                })
            };

            this.axiosPost({
                url: `${TENANT_BASE_URL}app/attendance-details/${this.attendanceDetail.id}/break-times/bulk`,
                data: data
            }).then(response => {
                this.$toastr.s('', response.data.message);
                this.$hub.$emit(`reload-${this.tableId || 'attendance-summery-table'}`);
                this.closeModal();
            }).catch(error => {
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors;
                }
                this.$toastr.e(error.response?.data?.message || error.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        extractTime(datetime) {
            // Convert UTC datetime from server to local time with user's preferred format
            return datetime ? onlyTime(datetime) : null;
        },
        getTimeForSubmit(row, field, {notBefore = null} = {}) {
            const originalField = `original_${field}`;
            const displayedTime = row[field];
            const originalValue = row[originalField];
            
            // If this is a new row (no original value), convert the new time
            if (!originalValue) {
                return this.formatDateTime(displayedTime, {notBefore});
            }
            
            // Extract time from original to compare (HH:mm format)
            const originalTimeDisplay = this.extractTime(originalValue);
            
            // If time hasn't changed, use original value to preserve seconds
            if (displayedTime === originalTimeDisplay) {
                return originalValue;
            }
            
            // Time was changed, convert the new time
            return this.formatDateTime(displayedTime, {notBefore});
        },
        formatDateTime(time, {notBefore = null} = {}) {
            const parsedTime = moment(time, ['HH:mm:ss', 'HH:mm', 'H:mm', 'hh:mm A', 'h:mm A'], true);

            if (!parsedTime.isValid()) {
                return time;
            }

            const attendanceStart = this.attendanceDetail.in_time
                ? moment(dateTimeToLocalWithFormat(this.attendanceDetail.in_time), 'YYYY-MM-DD HH:mm:ss')
                : moment();
            const attendanceEnd = this.attendanceDetail.out_time
                ? moment(dateTimeToLocalWithFormat(this.attendanceDetail.out_time), 'YYYY-MM-DD HH:mm:ss')
                : moment();
            const referenceStart = notBefore
                ? moment(dateTimeToLocalWithFormat(notBefore), 'YYYY-MM-DD HH:mm:ss')
                : null;
            const candidateDates = [attendanceStart.clone().startOf('day')];

            if (!attendanceEnd.isSame(attendanceStart, 'day')) {
                candidateDates.push(attendanceEnd.clone().startOf('day'));
            }

            const candidates = candidateDates
                .map(date => date.clone()
                    .hour(parsedTime.hour())
                    .minute(parsedTime.minute())
                    .second(parsedTime.second())
                    .millisecond(0))
                .filter(candidate => {
                    if (candidate.isBefore(attendanceStart) || candidate.isAfter(attendanceEnd)) {
                        return false;
                    }

                    if (referenceStart && candidate.isBefore(referenceStart)) {
                        return false;
                    }

                    return true;
                })
                .sort((first, second) => first.valueOf() - second.valueOf());

            const selected = candidates[0];

            if (!selected) {
                const fallbackDate = attendanceStart.clone().startOf('day');
                const fallback = fallbackDate
                    .hour(parsedTime.hour())
                    .minute(parsedTime.minute())
                    .second(parsedTime.second())
                    .millisecond(0);

                if (referenceStart && fallback.isBefore(referenceStart)) {
                    fallback.add(1, 'day');
                }

                return formatDateTimeForServer(fallback.format('YYYY-MM-DD HH:mm:ss'));
            }

            return formatDateTimeForServer(selected.format('YYYY-MM-DD HH:mm:ss'));
        },
        getError(index, field) {
            const key = `break_times.${index}.${field}`;
            if (!this.errors[key]) return '';
            // Simplify error message by removing field name prefix and adding "This field"
            let message = this.errors[key][0];
            return this.$t('this_field') + ' ' + message.replace(/^The .+ field /, '').replace(/^.+ field /, '');
        },
        closeModal() {
            $("#attendance-break-time-modal").modal('hide');
            this.$emit('input', false);
        }
    }
}
</script>
